import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Upload, message, Row, Col, Input, Form, Divider, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined, EyeOutlined } from '@ant-design/icons';
import RichTextEditor from '../../UI/RichTextEditor';
import api from '../../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

function SimpleHeroBanners() {
    const [heroSlides, setHeroSlides] = useState([]);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const loadData = async () => {
        try {
            setLoading(true);
            console.log('Loading hero slides and banners...');
            
            // Load hero slides from content management first
            try {
                const heroContent = await api.adminGetContentByKey('hero_slides');
                console.log('Hero content response:', heroContent);
                
                if (heroContent.success && heroContent.data) {
                    // Handle both direct content and nested structure
                    let contentData = heroContent.data.content || heroContent.data.hero_slides?.content;
                    if (contentData) {
                        const parsedHero = JSON.parse(contentData);
                        setHeroSlides(parsedHero);
                        console.log('Loaded hero slides from content:', parsedHero);
                    } else {
                        // Fallback to API
                        const heroResponse = await api.getHeroSlides().catch(() => ({ data: [] }));
                        const heroData = heroResponse.data?.data || heroResponse.data || [];
                        setHeroSlides(heroData);
                        console.log('Loaded hero slides from API:', heroData);
                    }
                } else {
                    // Fallback to API
                    const heroResponse = await api.getHeroSlides().catch(() => ({ data: [] }));
                    const heroData = heroResponse.data?.data || heroResponse.data || [];
                    setHeroSlides(heroData);
                    console.log('Loaded hero slides from API:', heroData);
                }
            } catch (error) {
                console.error('Error loading hero slides:', error);
                setHeroSlides([]);
            }

            // Load banners from content management first
            try {
                const bannerContent = await api.adminGetContentByKey('banners_media');
                console.log('Banner content response:', bannerContent);
                
                if (bannerContent.success && bannerContent.data) {
                    // Handle both direct content and nested structure
                    let contentData = bannerContent.data.content || bannerContent.data.banners_media?.content;
                    if (contentData) {
                        const parsedBanners = JSON.parse(contentData);
                        setBanners(parsedBanners);
                        console.log('Loaded banners from content:', parsedBanners);
                    } else {
                        // Fallback to API
                        const bannerResponse = await api.getPublicMediaByCategory('banner').catch(() => ({ data: [] }));
                        const bannerData = bannerResponse.data?.data || bannerResponse.data || [];
                        setBanners(bannerData);
                        console.log('Loaded banners from API:', bannerData);
                    }
                } else {
                    // Fallback to API
                    const bannerResponse = await api.getPublicMediaByCategory('banner').catch(() => ({ data: [] }));
                    const bannerData = bannerResponse.data?.data || bannerResponse.data || [];
                    setBanners(bannerData);
                    console.log('Loaded banners from API:', bannerData);
                }
            } catch (error) {
                console.error('Error loading banners:', error);
                setBanners([]);
            }

        } catch (error) {
            console.error('Failed to load data:', error);
            message.error('Failed to load hero slides and banners');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleHeroUpload = async (file, slideIndex) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', 'hero-slider');
            formData.append('alt_text', file.name);
            formData.append('is_public', '1');
            
            const response = await api.adminUploadMedia(formData);
            console.log('Hero upload response:', response);
            
            const uploadedUrl = response.data?.url || response.data?.original_url || response.data;
            
            const updatedSlides = [...heroSlides];
            updatedSlides[slideIndex] = {
                ...updatedSlides[slideIndex],
                url: uploadedUrl,
                image: uploadedUrl,
                id: response.data?.id || Date.now()
            };
            setHeroSlides(updatedSlides);
            
            message.success('Hero slide uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Failed to upload hero slide: ' + (error.message || 'Unknown error'));
        }
    };

    const handleBannerUpload = async (file, bannerIndex) => {
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('category', 'banner');
            formData.append('alt_text', file.name);
            formData.append('is_public', '1');
            
            const response = await api.adminUploadMedia(formData);
            console.log('Banner upload response:', response);
            
            const uploadedUrl = response.data?.url || response.data?.original_url || response.data;
            
            const updatedBanners = [...banners];
            updatedBanners[bannerIndex] = {
                ...updatedBanners[bannerIndex],
                url: uploadedUrl,
                id: response.data?.id || Date.now()
            };
            setBanners(updatedBanners);
            
            message.success('Banner uploaded successfully');
        } catch (error) {
            console.error('Upload error:', error);
            message.error('Failed to upload banner: ' + (error.message || 'Unknown error'));
        }
    };

    const addHeroSlide = () => {
        const newSlide = {
            id: Date.now(),
            title: '',
            heading: '',
            subheading: '',
            caption: '',
            url: '',
            image: '',
            cta_text: '',
            cta_url: '',
            overlay: 'dark',
            align: 'center'
        };
        setHeroSlides([...heroSlides, newSlide]);
    };

    const addBanner = () => {
        const newBanner = {
            id: Date.now(),
            alt_text: '',
            url: '',
            description: ''
        };
        setBanners([...banners, newBanner]);
    };

    const removeHeroSlide = (index) => {
        setHeroSlides(heroSlides.filter((_, i) => i !== index));
    };

    const removeBanner = (index) => {
        setBanners(banners.filter((_, i) => i !== index));
    };

    const updateHeroSlide = (index, field, value) => {
        const updated = [...heroSlides];
        updated[index] = { ...updated[index], [field]: value };
        setHeroSlides(updated);
    };

    const updateBanner = (index, field, value) => {
        const updated = [...banners];
        updated[index] = { ...updated[index], [field]: value };
        setBanners(updated);
    };

    const saveData = async () => {
        try {
            setSaving(true);
            console.log('Saving hero slides:', heroSlides);
            console.log('Saving banners:', banners);
            
            // Save hero slides as content
            try {
                const existing = await api.adminGetContentByKey('hero_slides');
                if (existing.success && existing.data) {
                    // Update existing
                    await api.adminUpdateContent(existing.data.id, {
                        content: JSON.stringify(heroSlides)
                    });
                    console.log('Updated existing hero slides content');
                } else {
                    // Create new
                    await api.adminCreateContent({
                        section: 'hero',
                        key: 'hero_slides',
                        type: 'json',
                        is_active: true,
                        title: 'Homepage Hero Slides',
                        content: JSON.stringify(heroSlides)
                    });
                    console.log('Created new hero slides content');
                }
            } catch (error) {
                console.error('Error saving hero slides:', error);
                throw error;
            }

            // Save banners as content
            try {
                const existing = await api.adminGetContentByKey('banners_media');
                if (existing.success && existing.data) {
                    // Update existing
                    await api.adminUpdateContent(existing.data.id, {
                        content: JSON.stringify(banners)
                    });
                    console.log('Updated existing banners content');
                } else {
                    // Create new
                    await api.adminCreateContent({
                        section: 'banners',
                        key: 'banners_media',
                        type: 'json',
                        is_active: true,
                        title: 'Homepage Banners',
                        content: JSON.stringify(banners)
                    });
                    console.log('Created new banners content');
                }
            } catch (error) {
                console.error('Error saving banners:', error);
                throw error;
            }

            message.success('Hero slides and banners saved successfully!');
            loadData();
        } catch (error) {
            console.error('Save error:', error);
            message.error('Failed to save data: ' + (error.message || 'Unknown error'));
        } finally {
            setSaving(false);
        }
    };

    const previewHomepage = () => {
        window.open('/?t=' + Date.now(), '_blank');
    };

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <Title level={2}>Hero Slides & Banners</Title>
                    <Text type="secondary">
                        Manage homepage hero slides and promotional banners. Upload images and configure their content.
                    </Text>
                </div>
                <Space>
                    <Button onClick={loadData} loading={loading}>
                        Refresh
                    </Button>
                    <Button type="primary" onClick={saveData} loading={saving}>
                        Save Changes
                    </Button>
                    <Button icon={<EyeOutlined />} onClick={previewHomepage}>
                        Preview Homepage
                    </Button>
                </Space>
            </div>

            <Alert
                message="Quick Setup Guide"
                description="1. Add hero slides for the homepage banner 2. Add banners for promotional content 3. Upload images for each item 4. Configure text content 5. Save and preview"
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            {/* Hero Slides Section */}
            <Card 
                title="Hero Slides" 
                extra={
                    <Space>
                        <Button icon={<PlusOutlined />} onClick={addHeroSlide}>
                            Add Slide
                        </Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                            // Add 3 slides at once
                            for (let i = 0; i < 3; i++) {
                                addHeroSlide();
                            }
                        }}>
                            Add Multiple Slides
                        </Button>
                    </Space>
                }
                style={{ marginBottom: 24 }}
            >
                {heroSlides.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No hero slides added yet.</p>
                        <p>Click "Add Hero Slide" to get started.</p>
                    </div>
                ) : (
                    <Row gutter={[16, 16]}>
                        {heroSlides.map((slide, index) => (
                            <Col xs={24} lg={12} key={slide.id || index}>
                                <Card size="small" className="h-full">
                                    <Row gutter={12}>
                                        <Col span={10}>
                                            <div className="mb-3">
                                                {slide.url || slide.image ? (
                                                    <img 
                                                        src={slide.url || slide.image} 
                                                        alt={slide.title || `Slide ${index + 1}`}
                                                        style={{ 
                                                            width: '100%', 
                                                            height: 120, 
                                                            objectFit: 'cover', 
                                                            borderRadius: 8,
                                                            border: '1px solid #eee'
                                                        }}
                                                        onError={(e) => {
                                                            e.currentTarget.src = '/images/placeholder.svg';
                                                        }}
                                                    />
                                                ) : (
                                                    <div 
                                                        style={{ 
                                                            width: '100%', 
                                                            height: 120, 
                                                            backgroundColor: '#f5f5f5',
                                                            borderRadius: 8,
                                                            border: '1px dashed #d9d9d9',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: '#999'
                                                        }}
                                                    >
                                                        No Image
                                                    </div>
                                                )}
                                            </div>
                                            <Upload
                                                accept="image/*"
                                                showUploadList={false}
                                                customRequest={({ file }) => handleHeroUpload(file, index)}
                                            >
                                                <Button size="small" icon={<UploadOutlined />} block>
                                                    Upload Image
                                                </Button>
                                            </Upload>
                                        </Col>
                                        <Col span={14}>
                                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                <Input
                                                    placeholder="Title"
                                                    value={slide.title}
                                                    onChange={(e) => updateHeroSlide(index, 'title', e.target.value)}
                                                    size="small"
                                                />
                                                <Input
                                                    placeholder="Heading"
                                                    value={slide.heading}
                                                    onChange={(e) => updateHeroSlide(index, 'heading', e.target.value)}
                                                    size="small"
                                                />
                                                <RichTextEditor
                                                    value={slide.subheading}
                                                    onChange={(value) => updateHeroSlide(index, 'subheading', value)}
                                                    placeholder="Subheading (supports formatting)"
                                                    rows={2}
                                                />
                                                <Input
                                                    placeholder="CTA Text"
                                                    value={slide.cta_text}
                                                    onChange={(e) => updateHeroSlide(index, 'cta_text', e.target.value)}
                                                    size="small"
                                                />
                                                <Input
                                                    placeholder="CTA URL"
                                                    value={slide.cta_url}
                                                    onChange={(e) => updateHeroSlide(index, 'cta_url', e.target.value)}
                                                    size="small"
                                                />
                                                <Button 
                                                    danger 
                                                    size="small" 
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => removeHeroSlide(index)}
                                                >
                                                    Remove
                                                </Button>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Card>

            {/* Banners Section */}
            <Card 
                title="Promotional Banners" 
                extra={
                    <Space>
                        <Button icon={<PlusOutlined />} onClick={addBanner}>
                            Add Banner
                        </Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                            // Add 3 banners at once
                            for (let i = 0; i < 3; i++) {
                                addBanner();
                            }
                        }}>
                            Add Multiple Banners
                        </Button>
                    </Space>
                }
            >
                {banners.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No banners added yet.</p>
                        <p>Click "Add Banner" to get started.</p>
                    </div>
                ) : (
                    <Row gutter={[16, 16]}>
                        {banners.map((banner, index) => (
                            <Col xs={24} lg={8} key={banner.id || index}>
                                <Card size="small" className="h-full">
                                    <div className="mb-3">
                                        {banner.url ? (
                                            <img 
                                                src={banner.url} 
                                                alt={banner.alt_text || `Banner ${index + 1}`}
                                                style={{ 
                                                    width: '100%', 
                                                    height: 120, 
                                                    objectFit: 'cover', 
                                                    borderRadius: 8,
                                                    border: '1px solid #eee'
                                                }}
                                                onError={(e) => {
                                                    e.currentTarget.src = '/images/placeholder.svg';
                                                }}
                                            />
                                        ) : (
                                            <div 
                                                style={{ 
                                                    width: '100%', 
                                                    height: 120, 
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: 8,
                                                    border: '1px dashed #d9d9d9',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: '#999'
                                                }}
                                            >
                                                No Image
                                            </div>
                                        )}
                                    </div>
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <Upload
                                            accept="image/*"
                                            showUploadList={false}
                                            customRequest={({ file }) => handleBannerUpload(file, index)}
                                        >
                                            <Button size="small" icon={<UploadOutlined />} block>
                                                Upload Image
                                            </Button>
                                        </Upload>
                                        <Input
                                            placeholder="Alt Text"
                                            value={banner.alt_text}
                                            onChange={(e) => updateBanner(index, 'alt_text', e.target.value)}
                                            size="small"
                                        />
                                        <RichTextEditor
                                            value={banner.description}
                                            onChange={(value) => updateBanner(index, 'description', value)}
                                            placeholder="Description (supports formatting)"
                                            rows={2}
                                        />
                                        <Button 
                                            danger 
                                            size="small" 
                                            icon={<DeleteOutlined />}
                                            onClick={() => removeBanner(index)}
                                        >
                                            Remove
                                        </Button>
                                    </Space>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Card>
        </div>
    );
}

export default SimpleHeroBanners;
